import styled from '@emotion/styled';
import { ErrorsContextProvider, ValidationErrors } from 'components';
import { ROUTING_PATHS } from 'const';
import { useIntParam } from 'hooks';
import { Modal } from 'semantic-ui-react';
import { Link, generatePath, useLocation, useNavigate } from 'react-router';
import { Button, toast } from '@britishcouncil/react-admin';
import { useCallback, useEffect, useMemo } from 'react';
import { parseServerErrors } from 'utils';
import { lrwSessionStore } from 'store';

export const ReleaseLrwWithSpeakingSession = () => {
  const sessionId = useIntParam('sessionId');
  const location = useLocation();
  const { lrwSessionDetails } = ROUTING_PATHS;
  const [releaseLrwSession, { isSuccess: isLrwSuccess, error: lrwError, isLoading: isConfirmingLrw, reset: resetLrw }] =
    lrwSessionStore.service.useReleaseLrwSessionMutation();
  const [releaseLrwWithSpeaking, { isSuccess: isBothSuccess, error: bothError, isLoading: isConfirmingBoth, reset: resetBoth }] =
    lrwSessionStore.service.useReleaseLrwWithSpeakingSessionMutation();
  const navigate = useNavigate();

  const isConfirming = isConfirmingLrw || isConfirmingBoth;
  const isSuccess = isLrwSuccess || isBothSuccess;
  const savingError = lrwError ?? bothError;
  const errors = useMemo(() => parseServerErrors(savingError), [savingError]);

  const goToLrwSessionDetails = useCallback(() => {
    navigate(generatePath(lrwSessionDetails.absolutePath, { sessionId }), {
      state: location.state,
    });
  }, [location.state, navigate, lrwSessionDetails.absolutePath, sessionId]);

  useEffect(() => {
    if (isSuccess) {
      toast({
        type: 'success',
        title: 'Success',
        message: 'Session released successfully',
      });
      goToLrwSessionDetails();
      resetLrw();
      resetBoth();
    }
  }, [goToLrwSessionDetails, isSuccess, sessionId, resetLrw, resetBoth]);

  return (
    <ErrorsContextProvider generalErrors={errors}>
      <Modal open size="tiny">
        <Modal.Header>Release session</Modal.Header>
        <Modal.Content>
          <S.ValidationErrors />
          <p>
            This LRW session is linked to unreleased Speaking session. Do you want to release this
            LRW session or both?
          </p>
        </Modal.Content>

        <Modal.Actions>
          <Link
            to={generatePath(lrwSessionDetails.absolutePath, { sessionId })}
            state={location.state}
          >
            <Button compact type="reset" secondary>
              Cancel
            </Button>
          </Link>

          <Button
            compact
            color="blue"
            onClick={() => {
              releaseLrwSession({ lrwSessionId: sessionId });
            }}
            loading={isConfirmingLrw}
            disabled={isConfirming}
          >
            Release LRW
          </Button>

          <Button
            compact
            color="blue"
            onClick={() => {
              releaseLrwWithSpeaking({ lrwSessionId: sessionId });
            }}
            loading={isConfirmingBoth}
            disabled={isConfirming}
          >
            Release LRW+S
          </Button>
        </Modal.Actions>
      </Modal>
    </ErrorsContextProvider>
  );
};

const S = {
  ValidationErrors: styled(ValidationErrors)`
    &.ui.error.message {
      margin-bottom: 3rem;
    }
  `,
};
